/**
 * SuperAdmin system health (SA-1). High-level only; secrets are sanitized in logs.
 */
import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../i18n';
import { interpolate } from '../locales/superAdmin';

const statusColor = (ok) => (ok ? '#3fb950' : '#f85149');
const DATE_LOCALES = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-SE' };

function formatUptime(seconds, healthText) {
    const s = Math.max(0, Math.floor(seconds || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h > 0) return interpolate(healthText.hoursMinutes, { hours: h, minutes: m });
    return interpolate(healthText.minutesSeconds, { minutes: m, seconds: s % 60 });
}

const SuperAdminHealthTab = () => {
    const { t, language } = useI18n();
    const healthText = t.superAdmin.health;
    const [health, setHealth] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiFetch('/api/superadmin/health');
            if (!res.ok) throw new Error(healthText.loadFailed);
            setHealth(await res.json());
        } catch (err) {
            setError(err.message || t.superAdmin.common.error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ color: '#f0f6fc', margin: 0 }}>
                    <Activity size={20} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    {healthText.title}
                </h3>
                <button className="btn-secondary" onClick={load} disabled={loading} style={{ padding: '0.4rem 0.8rem' }}>
                    {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
                    {' '}{healthText.refresh}
                </button>
            </div>
            <p style={{ color: '#8b949e', marginBottom: '1.5rem' }}>
                {healthText.description}
            </p>
            {error && <p style={{ color: '#f85149' }}>{error}</p>}
            {loading && !health && (
                <p style={{ color: '#8b949e' }}><Loader2 size={16} className="spin" /> {t.superAdmin.common.loading}</p>
            )}
            {health && (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                        {[
                            { label: healthText.database, ok: health.database?.ok, detail: health.database?.ok ? `${health.database.latencyMs} ms` : (health.database?.error || t.superAdmin.common.error) },
                            { label: healthText.email, ok: health.smtp?.ok, detail: health.smtp?.ok ? healthText.connectionOk : healthText.noConnection },
                            { label: healthText.processUptime, ok: true, detail: formatUptime(health.process?.uptimeSeconds, healthText) },
                            { label: healthText.memory, ok: true, detail: health.process?.rssMb != null ? interpolate(healthText.megabytes, { count: health.process.rssMb }) : '–' }
                        ].map((card) => (
                            <div key={card.label} style={{
                                background: 'rgba(0,0,0,0.25)',
                                border: `1px solid ${card.ok ? '#238636' : '#da3633'}`,
                                borderRadius: '8px',
                                padding: '1rem'
                            }}>
                                <div style={{ color: '#8b949e', fontSize: '0.75rem', marginBottom: '0.35rem' }}>{card.label}</div>
                                <div style={{ color: statusColor(card.ok), fontWeight: 600 }}>{card.detail}</div>
                            </div>
                        ))}
                    </div>
                    <h4 style={{ color: '#f0f6fc', margin: '0 0 0.75rem' }}>
                        <AlertTriangle size={16} style={{ marginRight: '0.4rem', verticalAlign: 'middle' }} />
                        {healthText.recentErrors}
                    </h4>
                    {(!health.recentErrors || health.recentErrors.length === 0) ? (
                        <p style={{ color: '#8b949e' }}>{healthText.noErrors}</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {health.recentErrors.map((entry, idx) => (
                                <div key={`${entry.at}-${idx}`} style={{
                                    background: 'rgba(0,0,0,0.25)',
                                    border: '1px solid #30363d',
                                    borderRadius: '8px',
                                    padding: '0.75rem'
                                }}>
                                    <div style={{ color: '#8b949e', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                                        {new Date(entry.at).toLocaleString(DATE_LOCALES[language] || DATE_LOCALES.fi)} · {entry.source}
                                    </div>
                                    <code style={{ color: '#f0f6fc', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{entry.message}</code>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default SuperAdminHealthTab;
